import { Card } from "@/components/ui/card";
import { useState, useEffect } from "react";
import { SmartfileSessionRow } from "./SmartfileSessionRow";
import { SmartfileSessionDetails } from "./SmartfileSessionDetails";
import { SmartfileTrainingPlanDetails } from "./SmartfileTrainingPlanDetails";
import { SmartfileMealPlanDetails } from "./SmartfileMealPlanDetails";
import { SmartfileMealPlanFoodDetails } from "./SmartfileMealPlanFoodDetails";
import { SmartfilePhysiotherapyEvolutionDetails } from "./SmartfilePhysiotherapyEvolutionDetails";
import { SmartfileAnthropometryDetails } from "./SmartfileAnthropometryDetails";
import { SmartfileAnamnesisDetails } from "./SmartfileAnamnesisDetails";
import { SmartfileMedicalReportDetails } from "./SmartfileMedicalReportDetails";
import { SmartfileVitalsDetails } from "./SmartfileVitalsDetails";
import { SmartfileAnatomopathologyDetails } from "./SmartfileAnatomopathologyDetails";
import { SmartfileBiopsyDetails } from "./SmartfileBiopsyDetails";
import { GalleryImageRow } from "./GalleryImageRow";
import { GalleryImageDetails } from "./GalleryImageDetails";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useModalStore } from "@/contexts/ModalContext";

interface SmartfileSession {
  id: string;
  file_name: string;
  content_type: string | null;
  ai_summary?: string | null;
  created_at: string;
  structured_records?: any[] | null;
  patient_id: string | null;
  status: string | null;
}

interface GalleryImage {
  id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  source: "upload" | "camera";
  ai_summary?: string | null;
  created_at: string;
}

interface PatientSmartfileRecordsListProps {
  sessions: SmartfileSession[];
  galleryImages?: GalleryImage[];
  onRefresh: () => void;
}

export function PatientSmartfileRecordsList({ sessions, galleryImages = [], onRefresh }: PatientSmartfileRecordsListProps) {
  const { openSessionId, setOpenSessionId } = useModalStore();
  const [expandAllSessionId, setExpandAllSessionId] = useState<string | null>(null);
  const [openImageId, setOpenImageId] = useState<string | null>(null);
  const [editingDateId, setEditingDateId] = useState<string | null>(null);

  // Reset expandAllSessionId when a session is closed
  useEffect(() => {
    if (!openSessionId) {
      setExpandAllSessionId(null);
    } else if (openSessionId !== expandAllSessionId) {
      // If a different session is opened via normal toggle (not records button), reset expandAll
      setExpandAllSessionId(null);
    }
  }, [openSessionId]);

  // Auto-scroll to records when expandAllSessionId changes
  useEffect(() => {
    if (expandAllSessionId) {
      const timer = setTimeout(() => {
        const element = document.getElementById(`records-content-${expandAllSessionId}`);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [expandAllSessionId]);

  // Combinar sessões e imagens, ordenar por data
  const allItems = [
    ...sessions.map(s => ({ type: 'session' as const, id: s.id, date: s.created_at, data: s })),
    ...galleryImages.map(i => ({ type: 'image' as const, id: i.id, date: i.created_at, data: i }))
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  if (allItems.length === 0) {
    return null;
  }

  // Contar items (lab_result + training_plan + meal_plan + physiotherapy_evolution + anthropometry + anamnesis) por sessão
  const getItemsCount = (session: SmartfileSession): number => {
    if (!session.structured_records || !Array.isArray(session.structured_records)) {
      return 0;
    }
    return session.structured_records.filter((record: any) => {
      if (typeof record !== 'object' || record === null || !('type' in record)) {
        return false;
      }
      const recordType = record.type;
      if (recordType === 'meal_plan') {
        const mealPlanRecord = record as { structure_type?: string; is_main_record?: boolean | string };
        const isMainRecord = mealPlanRecord.is_main_record === true || mealPlanRecord.is_main_record === 'true';
        return (mealPlanRecord.structure_type === 'weekly' || mealPlanRecord.structure_type === 'food') && !isMainRecord;
      }
      if (recordType === 'training_plan') {
        return !(record as any).is_main_record;
      }
      if (recordType === 'physiotherapy_evolution') {
        const physioRecord = record as { is_main_record?: boolean | string };
        const isMainRecord = physioRecord.is_main_record === true || physioRecord.is_main_record === 'true';
        return !isMainRecord;
      }
      if (recordType === 'anthropometry') {
        const anthropometryRecord = record as { is_main_record?: boolean | string };
        const isMainRecord = anthropometryRecord.is_main_record === true || anthropometryRecord.is_main_record === 'true';
        return !isMainRecord;
      }
      if (recordType === 'anamnesis') {
        const anamnesisRecord = record as { is_main_record?: boolean | string };
        const isMainRecord = anamnesisRecord.is_main_record === true || anamnesisRecord.is_main_record === 'true';
        return !isMainRecord;
      }
      if (recordType === 'medical_report') {
        const medicalReportRecord = record as { is_main_record?: boolean | string };
        const isMainRecord = medicalReportRecord.is_main_record === true || medicalReportRecord.is_main_record === 'true';
        return !isMainRecord;
      }
      if (recordType === 'vital_sign') {
        return true;
      }
      if (recordType === 'anatomopathology') {
        return true;
      }
      if (recordType === 'biopsy_report') {
        const biopsyRecord = record as { is_main_record?: boolean | string };
        const isMainRecord = biopsyRecord.is_main_record === true || biopsyRecord.is_main_record === 'true';
        return !isMainRecord;
      }
      return recordType === 'lab_result';
    }).length;
  };

  // Verificar se a sessão tem training_plan
  const hasTrainingPlan = (session: SmartfileSession): boolean => {
    if (!session.structured_records || !Array.isArray(session.structured_records)) {
      return false;
    }
    return session.structured_records.some((record: any) =>
      typeof record === 'object' && record !== null && 'type' in record && record.type === 'training_plan'
    );
  };

  // Verificar se a sessão tem meal_plan (weekly)
  const hasMealPlanWeekly = (session: SmartfileSession): boolean => {
    if (!session.structured_records || !Array.isArray(session.structured_records)) {
      return false;
    }
    return session.structured_records.some((record: any) => {
      if (typeof record !== 'object' || record === null || !('type' in record)) {
        return false;
      }
      if (record.type !== 'meal_plan') return false;
      const mealPlanRecord = record as { structure_type?: string; is_main_record?: boolean | string };
      const isMainRecord = mealPlanRecord.is_main_record === true || mealPlanRecord.is_main_record === 'true';
      return mealPlanRecord.structure_type === 'weekly' && !isMainRecord;
    });
  };

  // Verificar se a sessão tem meal_plan (food)
  const hasMealPlanFood = (session: SmartfileSession): boolean => {
    if (!session.structured_records || !Array.isArray(session.structured_records)) {
      return false;
    }
    return session.structured_records.some((record: any) => {
      if (typeof record !== 'object' || record === null || !('type' in record)) {
        return false;
      }
      if (record.type !== 'meal_plan') return false;
      const mealPlanRecord = record as { structure_type?: string; is_main_record?: boolean | string };
      const isMainRecord = mealPlanRecord.is_main_record === true || mealPlanRecord.is_main_record === 'true';
      return mealPlanRecord.structure_type === 'food' && !isMainRecord;
    });
  };

  // Verificar se a sessão tem physiotherapy_evolution (incluindo registro principal)
  const hasPhysiotherapyEvolution = (session: SmartfileSession): boolean => {
    if (!session.structured_records || !Array.isArray(session.structured_records)) {
      return false;
    }
    return session.structured_records.some((record: any) => {
      if (typeof record !== 'object' || record === null || !('type' in record)) {
        return false;
      }
      return record.type === 'physiotherapy_evolution';
    });
  };

  // Verificar se a sessão tem anthropometry
  const hasAnthropometry = (session: SmartfileSession): boolean => {
    if (!session.structured_records || !Array.isArray(session.structured_records)) {
      return false;
    }
    return session.structured_records.some((record: any) => {
      if (typeof record !== 'object' || record === null || !('type' in record)) {
        return false;
      }
      if (record.type !== 'anthropometry') return false;
      const anthropometryRecord = record as { is_main_record?: boolean | string };
      const isMainRecord = anthropometryRecord.is_main_record === true || anthropometryRecord.is_main_record === 'true';
      return !isMainRecord;
    });
  };

  // Verificar se a sessão tem anamnesis
  const hasAnamnesis = (session: SmartfileSession): boolean => {
    if (!session.structured_records || !Array.isArray(session.structured_records)) {
      return false;
    }
    return session.structured_records.some((record: any) => {
      if (typeof record !== 'object' || record === null || !('type' in record)) {
        return false;
      }
      if (record.type !== 'anamnesis') return false;
      const anamnesisRecord = record as { is_main_record?: boolean | string };
      const isMainRecord = anamnesisRecord.is_main_record === true || anamnesisRecord.is_main_record === 'true';
      return !isMainRecord;
    });
  };

  // Verificar se a sessão tem medical_report
  const hasMedicalReport = (session: SmartfileSession): boolean => {
    if (!session.structured_records || !Array.isArray(session.structured_records)) {
      return false;
    }
    return session.structured_records.some((record: any) => {
      if (typeof record !== 'object' || record === null || !('type' in record)) {
        return false;
      }
      return record.type === 'medical_report';
    });
  };

  // Verificar se a sessão tem anatomopathology
  const hasAnatomopathology = (session: SmartfileSession): boolean => {
    if (!session.structured_records || !Array.isArray(session.structured_records)) {
      return false;
    }
    return session.structured_records.some((record: any) => {
      if (typeof record !== 'object' || record === null || !('type' in record)) {
        return false;
      }
      return record.type === 'anatomopathology';
    });
  };

  // Verificar se a sessão tem vital_sign
  const hasVitals = (session: SmartfileSession): boolean => {
    if (!session.structured_records || !Array.isArray(session.structured_records)) {
      return false;
    }
    return session.structured_records.some((record: any) => {
      if (typeof record !== 'object' || record === null || !('type' in record)) {
        return false;
      }
      return record.type === 'vital_sign';
    });
  };

  // Verificar se a sessão tem biopsy_report
  const hasBiopsyReport = (session: SmartfileSession): boolean => {
    if (!session.structured_records || !Array.isArray(session.structured_records)) {
      return false;
    }
    return session.structured_records.some((record: any) => {
      if (typeof record !== 'object' || record === null || !('type' in record)) {
        return false;
      }
      return record.type === 'biopsy_report';
    });
  };

  return (
    <Card className="overflow-x-auto">
      {/* Header */}
      <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-muted/50 border-b text-xs font-medium text-muted-foreground uppercase min-w-[800px]">
        <div className="col-span-1"></div>
        <div className="col-span-4 md:col-span-2">Data</div>
        <div className="col-span-7 md:col-span-2">Tipo</div>
        <div className="hidden md:block md:col-span-3">Documento</div>
        <div className="hidden md:block md:col-span-4 text-left">Ações</div>
      </div>

      {/* Sessions e Images */}
      <div className="divide-y">
        {allItems.map((item) => {
          if (item.type === 'image') {
            const image = item.data as GalleryImage;
            return (
              <Collapsible
                key={`image-${image.id}`}
                open={openImageId === image.id}
                onOpenChange={(open) => {
                  setOpenImageId(open ? image.id : null);
                }}
              >
                <CollapsibleTrigger asChild>
                  <div className="hover:bg-muted/30 transition-colors border-b last:border-b-0">
                    <GalleryImageRow
                      image={image}
                      isOpen={openImageId === image.id}
                      onToggle={() => {
                        setOpenImageId(openImageId === image.id ? null : image.id);
                      }}
                      onDelete={onRefresh}
                      onUpdate={onRefresh}
                      isEditingDate={editingDateId === `image-${image.id}`}
                      onStartEditDate={() => setEditingDateId(`image-${image.id}`)}
                      onCancelEditDate={() => setEditingDateId(null)}
                      onSaveDate={() => {
                        setEditingDateId(null);
                        onRefresh();
                      }}
                    />
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div id={`image-content-${image.id}`} className="scroll-mt-4">
                    <GalleryImageDetails image={image} onRefresh={onRefresh} />
                  </div>
                </CollapsibleContent>
              </Collapsible>
            );
          } else {
            const session = item.data as SmartfileSession;
            return (
              <Collapsible
                key={`session-${session.id}`}
                open={openSessionId === session.id}
                onOpenChange={(open) => {
                  setOpenSessionId(open ? session.id : null);
                  if (!open) {
                    setExpandAllSessionId(null);
                  } else if (expandAllSessionId === session.id) {
                    // Keep expand all if already set
                  } else {
                    setExpandAllSessionId(null);
                  }
                }}
              >
                <CollapsibleTrigger asChild>
                  <div className="hover:bg-muted/30 transition-colors border-b last:border-b-0">
                    <SmartfileSessionRow
                      session={session}
                      isOpen={openSessionId === session.id}
                      onToggle={() => {
                        setOpenSessionId(openSessionId === session.id ? null : session.id);
                      }}
                      onOpenRegistros={() => {
                        if (openSessionId === session.id && expandAllSessionId === session.id) {
                          setOpenSessionId(null);
                          setExpandAllSessionId(null);
                        } else {
                          setOpenSessionId(session.id);
                          setExpandAllSessionId(session.id);
                        }
                      }}
                      labResultsCount={getItemsCount(session)}
                      onDelete={onRefresh}
                      onUpdate={onRefresh}
                      isEditingDate={editingDateId === `session-${session.id}`}
                      onStartEditDate={() => setEditingDateId(`session-${session.id}`)}
                      onCancelEditDate={() => setEditingDateId(null)}
                      onSaveDate={() => {
                        setEditingDateId(null);
                        onRefresh();
                      }}
                    />
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div id={`records-content-${session.id}`} className="scroll-mt-4">
                    {hasMealPlanFood(session) ? (
                      <SmartfileMealPlanFoodDetails session={session} onRefresh={onRefresh} expandAllByDefault={expandAllSessionId === session.id} />
                    ) : hasMealPlanWeekly(session) ? (
                      <SmartfileMealPlanDetails session={session} onRefresh={onRefresh} expandAllByDefault={expandAllSessionId === session.id} />
                    ) : hasTrainingPlan(session) ? (
                      <SmartfileTrainingPlanDetails session={session} onRefresh={onRefresh} expandAllByDefault={expandAllSessionId === session.id} />
                    ) : hasPhysiotherapyEvolution(session) ? (
                      <SmartfilePhysiotherapyEvolutionDetails session={session} onRefresh={onRefresh} expandAllByDefault={expandAllSessionId === session.id} />
                    ) : hasAnamnesis(session) ? (
                      <SmartfileAnamnesisDetails session={session} onRefresh={onRefresh} expandAllByDefault={expandAllSessionId === session.id} />
                    ) : hasBiopsyReport(session) ? (
                      <SmartfileBiopsyDetails session={session} onRefresh={onRefresh} expandAllByDefault={expandAllSessionId === session.id} />
                    ) : hasMedicalReport(session) ? (
                      <SmartfileMedicalReportDetails session={session} onRefresh={onRefresh} expandAllByDefault={expandAllSessionId === session.id} />
                    ) : hasVitals(session) ? (
                      <SmartfileVitalsDetails session={session} onRefresh={onRefresh} expandAllByDefault={expandAllSessionId === session.id} />
                    ) : hasAnatomopathology(session) ? (
                      <SmartfileAnatomopathologyDetails session={session} onRefresh={onRefresh} expandAllByDefault={expandAllSessionId === session.id} />
                    ) : hasAnthropometry(session) ? (
                      <SmartfileAnthropometryDetails session={session} onRefresh={onRefresh} expandAllByDefault={expandAllSessionId === session.id} />
                    ) : (
                      <SmartfileSessionDetails session={session} onRefresh={onRefresh} expandAllByDefault={expandAllSessionId === session.id} />
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            );
          }
        })}
      </div>

    </Card>
  );
}

